import { useHttp } from "../http/useHttp";
import { useAxiosQuery, UseAxiosQueryOptions } from "../http/useAxiosQuery";
import { useMemo } from "react";
import { useDefaultError } from "@synota-io/synota-shared-ui";
import { paths } from "../generated-types";

type ServiceChargePaymentsOperation =
  paths["/dashboard/users/{api_token_uuid}/service-charges/payments"]["post"];
type UseServiceChargePaymentsResponse =
  ServiceChargePaymentsOperation["responses"][200]["content"]["application/json"];
type UseServiceChargePaymentsRequestBody =
  ServiceChargePaymentsOperation["requestBody"]["content"]["application/json"];
type ServiceChargePaymentsPath = ServiceChargePaymentsOperation["parameters"]["path"];

export type IServiceChargePayment = UseServiceChargePaymentsResponse["items"][number];
export type UseServiceChargePaymentsInput = UseServiceChargePaymentsRequestBody & {
  user_uuid: ServiceChargePaymentsPath["api_token_uuid"];
};

export const useServiceChargePayments = (
  input: UseServiceChargePaymentsInput = { user_uuid: "" },
  options: Omit<
    UseAxiosQueryOptions<UseServiceChargePaymentsResponse>,
    "queryKey" | "queryFn"
  > = {},
) => {
  const { client } = useHttp();

  const { status, isPending, isFetching, isLoading, data, error, refetch } =
    useAxiosQuery<UseServiceChargePaymentsResponse>({
      queryKey: ["user-service-charge-payments", input],
      queryFn: ({ signal }) => {
        return client.post(`/users/${input.user_uuid}/service-charges/payments`, input, { signal });
      },
      enabled: options.enabled !== false && !!input.user_uuid,
      ...options,
    });

  const [payments, hasNextPage] = useMemo(() => {
    if (!data) {
      return [[], false];
    }
    return [data.data.items, data.data.has_next_page];
  }, [data]);

  useDefaultError(error);

  return {
    payments,
    hasNextPage,
    error,
    isLoading,
    isFetching,
    isPending,
    status,
    refetch,
  };
};

export type UseServiceChargePaymentsReturn = ReturnType<typeof useServiceChargePayments>;
